import { I18n } from '@affine/i18n';
import type {
  GoalStepModelPolicy,
  GoalTreeModelTier,
  GoalTreeReasoningLevel,
} from '@streamlain/view-config';
import { css, html, LitElement, nothing } from 'lit';
import { property } from 'lit/decorators.js';

import {
  aiChatControlTypography,
  aiChatMetaTypography,
} from '../../styles/ai-chat-typography';
import { modelTierLabel, reasoningLabel } from './goal-tree-view-model';

const TIERS: GoalTreeModelTier[] = ['economy', 'standard', 'pro'];
const REASONING: GoalTreeReasoningLevel[] = ['off', 'low', 'medium', 'high'];

export class GoalStepModelControls extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }

    .model-controls {
      display: grid;
      gap: 8px;
    }

    .model-controls-row {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      align-items: center;
    }

    .model-controls-label {
      ${aiChatMetaTypography}
      min-width: 58px;
      color: var(--affine-text-secondary-color);
    }

    .model-controls-button,
    .model-controls-toggle {
      ${aiChatControlTypography}
      min-height: 28px;
      border: 1px solid var(--affine-border-color);
      border-radius: 6px;
      background: var(--affine-background-primary-color);
      color: var(--affine-text-primary-color);
      cursor: pointer;
    }

    .model-controls-button[data-active],
    .model-controls-toggle[data-active] {
      border-color: #2563eb;
      color: #1d4ed8;
    }

    .model-controls-button[disabled] {
      cursor: not-allowed;
      opacity: 0.45;
    }

    .model-controls-range {
      width: min(180px, 100%);
    }
  `;

  @property({ attribute: false })
  accessor policy: GoalStepModelPolicy | null = null;

  @property({ attribute: false })
  accessor onPolicyChange:
    | ((policy: GoalStepModelPolicy, manual: boolean) => void)
    | null = null;

  private get effectiveTier(): GoalTreeModelTier | null {
    return this.policy?.userOverride?.tier ?? this.policy?.defaultTier ?? null;
  }

  private get effectiveReasoning(): GoalTreeReasoningLevel {
    return (
      this.policy?.userOverride?.reasoning ?? this.policy?.reasoning ?? 'low'
    );
  }

  private updateTier(tier: GoalTreeModelTier) {
    if (!this.policy || !this.onPolicyChange) return;
    this.onPolicyChange(
      {
        ...this.policy,
        userOverride: {
          ...(this.policy.userOverride ?? {}),
          tier,
          updatedAt: new Date().toISOString(),
        },
      },
      true
    );
  }

  private updateReasoning(index: number) {
    if (!this.policy || !this.onPolicyChange) return;
    const reasoning = REASONING[index] ?? this.effectiveReasoning;
    this.onPolicyChange(
      {
        ...this.policy,
        userOverride: {
          ...(this.policy.userOverride ?? {}),
          reasoning,
          updatedAt: new Date().toISOString(),
        },
      },
      true
    );
  }

  private resetToAuto() {
    if (!this.policy || !this.onPolicyChange) return;
    this.onPolicyChange(
      {
        ...this.policy,
        userOverride: undefined,
      },
      false
    );
  }

  override render() {
    if (!this.policy) return nothing;
    const allowed = new Set(this.policy.allowedTiers);
    const activeTier = this.effectiveTier;
    const reasoningIndex = REASONING.indexOf(this.effectiveReasoning);
    return html`<div
      class="model-controls"
      aria-label=${I18n.t('com.streamlain.ai.target.model.controls')}
    >
      <div class="model-controls-row">
        <span class="model-controls-label"
          >${I18n.t('com.streamlain.ai.target.model.mode')}</span
        >
        <button
          class="model-controls-toggle"
          type="button"
          ?data-active=${!this.policy.userOverride}
          @click=${this.resetToAuto}
        >
          ${I18n.t('com.streamlain.ai.target.model.auto')}
        </button>
        <button
          class="model-controls-toggle"
          type="button"
          ?data-active=${!!this.policy.userOverride}
          @click=${() => activeTier && this.updateTier(activeTier)}
        >
          ${I18n.t('com.streamlain.ai.target.model.manual')}
        </button>
      </div>
      <div class="model-controls-row">
        <span class="model-controls-label"
          >${I18n.t('com.streamlain.ai.target.model.label')}</span
        >
        ${TIERS.map(
          tier =>
            html`<button
              class="model-controls-button"
              type="button"
              ?disabled=${!allowed.has(tier)}
              ?data-active=${activeTier === tier}
              @click=${() => this.updateTier(tier)}
            >
              ${modelTierLabel(tier)}
            </button>`
        )}
      </div>
      <div class="model-controls-row">
        <span class="model-controls-label"
          >${I18n.t('com.streamlain.ai.target.model.reasoning-label')}</span
        >
        <input
          class="model-controls-range"
          type="range"
          min="0"
          max="3"
          step="1"
          .value=${String(Math.max(0, reasoningIndex))}
          @input=${(event: InputEvent) =>
            this.updateReasoning(
              Number((event.currentTarget as HTMLInputElement).value)
            )}
        />
        <span>${reasoningLabel(this.effectiveReasoning)}</span>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'goal-step-model-controls': GoalStepModelControls;
  }
}
